<template>
  <svg
    ref="rootRef"
    class="block size-14"
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <!-- Active state: white pill with the house knocked out (via mask) -->
    <template v-if="active">
      <mask
        :id="maskId"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="56"
        height="56"
      >
        <rect width="56" height="56" fill="white" />
        <g class="house">
          <path :d="HOUSE_D" fill="black" />
          <!-- Shutters cover the white «window» marks, making them transparent (= closed) -->
          <rect
            v-for="(s, i) in SHUTTERS"
            :key="i"
            class="shutter"
            :class="s.row"
            :x="s.x"
            :y="s.y"
            :width="s.w"
            :height="s.h"
            fill="black"
          />
        </g>
      </mask>
      <rect
        width="56"
        height="56"
        rx="16"
        fill="white"
        :mask="`url(#${maskId})`"
      />
    </template>

    <!-- Inactive state: frosted background + white house -->
    <template v-else>
      <rect width="56" height="56" rx="16" fill="white" fill-opacity="0.3" />
      <g class="house">
        <path :d="HOUSE_D" fill="white" />
        <!-- Shutters (white) fill the dark openings top-to-bottom (= closed) -->
        <rect
          v-for="(s, i) in SHUTTERS"
          :key="i"
          class="shutter"
          :class="s.row"
          :x="s.x"
          :y="s.y"
          :width="s.w"
          :height="s.h"
          fill="white"
        />
      </g>
    </template>
  </svg>
</template>

<script setup lang="ts">
  import { ref, useId, watch } from 'vue';

  const props = defineProps<{ active: boolean; pulseKey?: number }>();

  const rootRef = ref<SVGSVGElement | null>(null);
  const maskId = `hotels-switch-${useId()}`;

  // House with knockout openings (evenodd) — coords are 1:1 from hotels-inactive.svg
  const HOUSE_D =
    'M37.25 36.5H35.94V24.91C35.94 23.44 35.1 22.18 33.89 21.54V20.8C33.89 19.25 32.63 18 31.08 18H24.92C23.37 18 22.11 19.25 22.11 20.8V21.54C20.9 22.18 20.06 23.44 20.06 24.91V36.5H18.75C18.34 36.5 18 36.83 18 37.25C18 37.66 18.34 38 18.75 38H37.25C37.66 38 38 37.66 38 37.25C38 36.83 37.66 36.5 37.25 36.5ZM25.94 31.32H24.92C24.5 31.32 24.17 30.98 24.17 30.57V29.54C24.17 29.12 24.5 28.79 24.92 28.79H25.94C26.36 28.79 26.69 29.12 26.69 29.54V30.57C26.69 30.98 26.36 31.32 25.94 31.32ZM24.17 25.94V24.91C24.17 24.5 24.5 24.16 24.92 24.16H25.94C26.36 24.16 26.69 24.5 26.69 24.91V25.94C26.69 26.35 26.36 26.69 25.94 26.69H24.92C24.5 26.69 24.17 26.35 24.17 25.94ZM29.31 36.5H26.69V35.19C26.69 34.47 27.28 33.88 28 33.88C28.72 33.88 29.31 34.47 29.31 35.19V36.5ZM31.83 30.57C31.83 30.98 31.5 31.32 31.08 31.32H30.06C29.64 31.32 29.31 30.98 29.31 30.57V29.54C29.31 29.12 29.64 28.79 30.06 28.79H31.08C31.5 28.79 31.83 29.12 31.83 29.54V30.57ZM31.83 25.94C31.83 26.35 31.5 26.69 31.08 26.69H30.06C29.64 26.69 29.31 26.35 29.31 25.94V24.91C29.31 24.5 29.64 24.16 30.06 24.16H31.08C31.5 24.16 31.83 24.5 31.83 24.91V25.94ZM32.39 21.08H23.61V20.8C23.61 20.08 24.2 19.5 24.92 19.5H31.08C31.8 19.5 32.39 20.08 32.39 20.8V21.08Z';

  // Shutters over the openings (coords widened by ~0.2 to avoid an edge seam).
  // row → cascade delay top-to-bottom: top windows → bottom windows → door.
  const SHUTTERS = [
    { x: 23.97, y: 23.96, w: 2.92, h: 2.93, row: 'r-top' },
    { x: 29.11, y: 23.96, w: 2.92, h: 2.93, row: 'r-top' },
    { x: 23.97, y: 28.59, w: 2.92, h: 2.93, row: 'r-mid' },
    { x: 29.11, y: 28.59, w: 2.92, h: 2.93, row: 'r-mid' },
    { x: 26.49, y: 33.68, w: 3.02, h: 2.92, row: 'r-door' },
  ] as const;

  // Play the macro animation: toggle the class with a forced reflow → restart the CSS
  function play() {
    const el = rootRef.value;
    if (!el) return;
    el.classList.remove('is-playing');
    void el.getBoundingClientRect();
    el.classList.add('is-playing');
  }

  // Restart on the shared pulseKey counter (click + autoplay from AppSwitchButton)
  watch(() => props.pulseKey, play);
</script>

<style scoped>
  /* House: scales up a touch and stretches upward (like a growing building) with a bounce */
  .house {
    transform-box: fill-box;
    transform-origin: 50% 100%;
  }
  .is-playing .house {
    /* unified timing for house, shutters and rays: 720ms, «peak» at 30% */
    animation: hotel-macro 720ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }
  @keyframes hotel-macro {
    0% {
      transform: scale(1, 1);
    }
    30% {
      /* PEAK: narrows on the sides and stretches upward (squash & stretch) */
      transform: scale(0.9, 1.28);
    }
    50% {
      /* rebound: wider and shorter */
      transform: scale(1.05, 0.96);
    }
    70% {
      transform: scale(0.97, 1.04);
    }
    86% {
      transform: scale(1.01, 0.99);
    }
    100% {
      transform: scale(1, 1);
    }
  }

  /* Shutters: close top-to-bottom (origin — top edge), then open */
  .shutter {
    transform-box: fill-box;
    transform-origin: 50% 0%;
    transform: scaleY(0);
  }
  .is-playing .shutter {
    /* quick «blink» (closed → opened) during the building's growth, not a long close */
    animation: shutter-blink 300ms cubic-bezier(0.34, 1.4, 0.5, 1) both;
  }
  /* Top-to-bottom cascade: top windows → bottom windows → door (barely noticeable) */
  .is-playing .r-top {
    animation-delay: 0ms;
  }
  .is-playing .r-mid {
    animation-delay: 25ms;
  }
  .is-playing .r-door {
    animation-delay: 50ms;
  }
  @keyframes shutter-blink {
    0% {
      transform: scaleY(0);
    }
    40% {
      /* closed (~120ms) — while the building grows */
      transform: scaleY(1);
    }
    60% {
      transform: scaleY(1);
    }
    100% {
      /* and immediately opened (~300ms) — the blink ends near the growth peak */
      transform: scaleY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .is-playing .house,
    .is-playing .shutter {
      animation: none !important;
    }
    .shutter {
      transform: scaleY(0);
    }
  }
</style>
